import { Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ClientProfileWithCaseCount } from '../../types/case-management'
import type { DeleteOption } from './types'

interface OptionsBlockProps {
  selectedClient: ClientProfileWithCaseCount
  profiles: ClientProfileWithCaseCount[]
  deleteOption: DeleteOption
  setDeleteOption: (option: DeleteOption) => void
  reassignTargetId: string
  setReassignTargetId: (id: string) => void
}

function ReassignSelect(props: {
  profiles: ClientProfileWithCaseCount[]
  selectedClient: ClientProfileWithCaseCount
  reassignTargetId: string
  setReassignTargetId: (id: string) => void
}): React.ReactElement {
  const { t } = useTranslation('cases')
  const { profiles, selectedClient, reassignTargetId, setReassignTargetId } = props
  return (
    <select
      value={reassignTargetId}
      onChange={(event) => {
        setReassignTargetId(event.target.value)
      }}
      className="mt-2 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100"
    >
      <option value="">{t('clients.selectReassign')}</option>
      {profiles
        .filter((profile) => profile.id !== selectedClient.id && profile.status === 'active')
        .map((profile) => (
          <option key={profile.id} value={profile.id}>
            {profile.name}
          </option>
        ))}
    </select>
  )
}

function OptionsBlock(props: OptionsBlockProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { selectedClient, profiles, deleteOption, setDeleteOption } = props
  const { reassignTargetId, setReassignTargetId } = props
  return (
    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-900/20">
      <p className="mb-3 text-sm font-medium text-yellow-800 dark:text-yellow-400">
        {t('clients.linkedCasesWarning', { count: selectedClient.caseCount })}
      </p>
      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="deleteOption"
            checked={deleteOption === 'unlink'}
            onChange={() => {
              setDeleteOption('unlink')
            }}
            className="h-4 w-4 text-[#4e5e82]"
          />
          <span className="text-sm text-stone-700 dark:text-stone-300">
            {t('clients.unlinkOption')}
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="deleteOption"
            checked={deleteOption === 'reassign'}
            onChange={() => {
              setDeleteOption('reassign')
            }}
            className="h-4 w-4 text-[#4e5e82]"
          />
          <span className="text-sm text-stone-700 dark:text-stone-300">
            {t('clients.reassignOption')}
          </span>
        </label>
        {deleteOption === 'reassign' && (
          <ReassignSelect
            profiles={profiles}
            selectedClient={selectedClient}
            reassignTargetId={reassignTargetId}
            setReassignTargetId={setReassignTargetId}
          />
        )}
      </div>
    </div>
  )
}

export interface DeleteClientModalProps {
  selectedClient: ClientProfileWithCaseCount
  profiles: ClientProfileWithCaseCount[]
  deleteOption: DeleteOption
  setDeleteOption: (option: DeleteOption) => void
  reassignTargetId: string
  setReassignTargetId: (id: string) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function DeleteClientModal(props: DeleteClientModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { selectedClient, profiles, deleteOption, setDeleteOption } = props
  const { reassignTargetId, setReassignTargetId, formError, formLoading, onClose, onConfirm } =
    props
  const confirmDisabled = formLoading || (deleteOption === 'reassign' && !reassignTargetId)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-stone-900">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t('clients.deleteClient')}
          </h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {formError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
          </div>
        )}
        <p className="mb-4 text-sm text-stone-600 dark:text-stone-400">
          {t('clients.confirmDelete')}{' '}
          <strong className="text-stone-900 dark:text-stone-100">{selectedClient.name}</strong>?
        </p>
        {selectedClient.caseCount > 0 && (
          <OptionsBlock
            selectedClient={selectedClient}
            profiles={profiles}
            deleteOption={deleteOption}
            setDeleteOption={setDeleteOption}
            reassignTargetId={reassignTargetId}
            setReassignTargetId={setReassignTargetId}
          />
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {formLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('actions.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}
